import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';
import { useCompactLayout } from '../hooks/useCompactLayout';

export type StoryPhoto = {
  url: string;
  type?: 'image' | 'video';
  poster?: string;
  title?: string;
  narrative?: string;
};

type TimelineYear = {
  year: string | number;
  photoCount?: number;
  isActive?: boolean;
  activePhotoIndex?: number;
};

type StoryGalleryProps = {
  photos: StoryPhoto[];
  contextTitle?: string;
  contextSubtitle?: string;
  autoplay?: boolean;
  onCycleComplete?: () => void;
  onPlayChange?: (isPlaying: boolean) => void;
  externalPlayState?: boolean;
  fullscreen?: boolean;
  onPhotoChange?: (photo: StoryPhoto, index: number, instanceId?: string) => void;
  showBottomCaption?: boolean;
  timeline?: React.ReactNode;
  timelineYears?: TimelineYear[];
  timelineYearsHeading?: string;
  timelineYearsSubheading?: string;
  onTimelineYearSelect?: (year: string | number) => void;
  compactPlayState?: boolean;
  onCompactPlayToggle?: () => void;
  kenBurnsActive?: boolean;
  tintOverlay?: boolean;
  advanceSignal?: number;
  primaryControl?: React.ReactNode;
  instanceId?: string;
  requestedPhotoIndex?: number | null;
  sidebarRevealSignal?: number;
  sidebarRevealDurationMs?: number;
  onPrevGroup?: () => void;
  onNextGroup?: () => void;
  overallTotalSeconds?: number;
  overallRemainingSeconds?: number;
  durationLoadedCount?: number;
  durationExpectedCount?: number;
  onTimelinePhotoSelect?: (year: string | number, photoIndex: number) => void;
};

type StoryGalleryViewProps = {
  photosLength: number;
  activePhoto: StoryPhoto;
  activeMediaType: 'image' | 'video';
  compactLayout: boolean;
  fullscreen: boolean;
  mobileMediaFit: string;
  mobileMediaPosition: string;
  mobileMediaScale: string;
  shouldKenBurns: boolean;
  showBottomCaption: boolean;
  sidebarOpen: boolean;
  uiVisible: boolean;
  isPortrait: boolean;
  contextSubtitle?: string;
  contextTitle?: string;
  activeTimelineEntry?: TimelineYear;
  timelineYears?: TimelineYear[];
  timelineYearsHeading?: string;
  timelineYearsSubheading?: string;
  timeline?: React.ReactNode;
  primaryControl?: React.ReactNode;
  primaryIsPlaying: boolean;
  compactIsPlaying: boolean;
  overallTotalSeconds?: number;
  overallRemainingSeconds?: number;
  durationLoadedCount?: number;
  durationExpectedCount?: number;
  onTimelineYearSelect?: (year: string | number) => void;
  onTimelinePhotoSelect?: (year: string | number, photoIndex: number) => void;
  onPrimaryPlayToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onCompactPlayToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSidebarToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSidebarShow: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onPrevAction: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onNextAction: (event: React.MouseEvent<HTMLButtonElement>) => void;
  canNavigate: boolean;
  prevDisabled: boolean;
  nextDisabled: boolean;
  prevAriaLabel: string;
  nextAriaLabel: string;
  onTouchStart: (event: React.TouchEvent<HTMLDivElement>) => void;
  onTouchEnd: (event: React.TouchEvent<HTMLDivElement>) => void;
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void;
};

function StoryGalleryView({
  photosLength,
  activePhoto,
  activeMediaType,
  compactLayout,
  fullscreen,
  mobileMediaFit,
  mobileMediaPosition,
  mobileMediaScale,
  shouldKenBurns,
  showBottomCaption,
  sidebarOpen,
  uiVisible,
  isPortrait,
  contextSubtitle,
  contextTitle,
  activeTimelineEntry,
  timelineYears,
  timelineYearsHeading,
  timelineYearsSubheading,
  timeline,
  primaryControl,
  primaryIsPlaying,
  compactIsPlaying,
  overallTotalSeconds,
  overallRemainingSeconds,
  durationLoadedCount,
  durationExpectedCount,
  onTimelineYearSelect,
  onTimelinePhotoSelect,
  onPrimaryPlayToggle,
  onCompactPlayToggle,
  onSidebarToggle,
  onSidebarShow,
  onPrevAction,
  onNextAction,
  canNavigate,
  prevDisabled,
  nextDisabled,
  prevAriaLabel,
  nextAriaLabel,
  onTouchStart,
  onTouchEnd,
  onPointerDown,
  onMouseMove,
}: StoryGalleryViewProps) {
  const ESTIMATED_PHOTO_SECONDS = 8;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const totalPhotos = (timelineYears ?? []).reduce((sum, entry) => sum + (entry.photoCount ?? 0), 0);
  let overallCompleted = 0;
  if (timelineYears && timelineYears.length > 0) {
    for (const entry of timelineYears) {
      const count = entry.photoCount ?? 0;
      if (entry.isActive) {
        const activeIndex = entry.activePhotoIndex ?? 0;
        overallCompleted += Math.min(activeIndex + 1, Math.max(count, 0));
        break;
      }
      overallCompleted += count;
    }
  }
  const overallIndex = totalPhotos > 0 ? Math.max(overallCompleted - 1, 0) : 0;
  const elapsedInCurrent = Math.min(elapsedSeconds, ESTIMATED_PHOTO_SECONDS);
  const estimatedProgress =
    totalPhotos > 0 ? (overallIndex + elapsedInCurrent / ESTIMATED_PHOTO_SECONDS) / totalPhotos : 0;
  const estimatedRemainingSeconds = Math.max(
    (totalPhotos - (overallIndex + 1)) * ESTIMATED_PHOTO_SECONDS +
      (ESTIMATED_PHOTO_SECONDS - elapsedInCurrent),
    0
  );
  const hasRealTime = (overallTotalSeconds ?? 0) > 0;
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const overallProgress = hasRealTime
    ? (overallTotalSeconds! - (overallRemainingSeconds ?? 0)) / overallTotalSeconds!
    : estimatedProgress;
  const overallTimeLeft = hasRealTime ? overallRemainingSeconds ?? 0 : estimatedRemainingSeconds;
  const overallTimeLabel = hasRealTime
    ? `${formatTime(overallTimeLeft)} left`
    : durationExpectedCount && durationExpectedCount > 0
      ? `Loading audio durations (${durationLoadedCount ?? 0}/${durationExpectedCount})`
      : 'Calculating…';
  let yearStart = 0;
  const yearSegments = (timelineYears ?? []).map((entry) => {
    const count = entry.photoCount ?? 0;
    const startRatio = totalPhotos > 0 ? yearStart / totalPhotos : 0;
    yearStart += count;
    return { year: entry.year, startRatio };
  });
  let cumulative = 0;
  const overallTicks = (timelineYears ?? []).map((entry) => {
    const count = entry.photoCount ?? 0;
    cumulative += count;
    const ratio = totalPhotos > 0 ? cumulative / totalPhotos : 0;
    return { year: entry.year, ratio };
  });
  const handleTimelineClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onTimelinePhotoSelect || totalPhotos <= 0 || !timelineYears) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    const globalIndex = Math.min(Math.floor(ratio * totalPhotos), Math.max(totalPhotos - 1, 0));
    let acc = 0;
    for (const entry of timelineYears) {
      const count = entry.photoCount ?? 0;
      if (globalIndex < acc + count) {
        const photoIndex = Math.max(globalIndex - acc, 0);
        onTimelinePhotoSelect(entry.year, photoIndex);
        return;
      }
      acc += count;
    }
  };

  const fullscreenStyle = fullscreen
    ? {
        height: 'var(--story-vh, 100vh)',
        width: 'var(--story-vw, 100vw)',
        maxHeight: 'var(--story-vh, 100vh)',
        maxWidth: 'var(--story-vw, 100vw)',
      }
    : undefined;

  useEffect(() => {
    setElapsedSeconds(0);
  }, [activeTimelineEntry?.year, activeTimelineEntry?.activePhotoIndex, totalPhotos]);

  useEffect(() => {
    if (!compactIsPlaying || hasRealTime) return;
    const intervalId = window.setInterval(() => {
      setElapsedSeconds((prev) => Math.min(prev + 1, ESTIMATED_PHOTO_SECONDS));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [compactIsPlaying, hasRealTime]);

  return (
    <>
      {shouldKenBurns && (
        <style>{`
          @keyframes breathingScale {
            0% { transform: scale(1); }
            50% { transform: scale(1.02); }
            100% { transform: scale(1); }
          }
        `}</style>
      )}
      <div
        className={
          fullscreen
            ? 'relative w-screen overflow-hidden'
            : 'border border-slate-200 dark:border-slate-700 rounded-2xl p-0 overflow-hidden bg-white dark:bg-slate-800 min-h-[28rem]'
        }
        style={fullscreenStyle}
        onMouseMove={onMouseMove}
        onPointerDown={onPointerDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className={
            fullscreen
              ? `relative h-full w-full ${compactLayout ? 'flex flex-col' : 'md:block'}`
              : 'relative h-full w-full min-h-[28rem]'
          }
        >
          <div className="relative flex-1 overflow-hidden bg-black md:absolute md:inset-0">
            {activeMediaType === 'video' ? (
              <>
                <video
                  key={activePhoto.url}
                  src={activePhoto.url}
                  poster={activePhoto.poster}
                  className={`${compactLayout ? 'block' : 'md:hidden'} w-full h-full ${mobileMediaFit} ${mobileMediaPosition} ${mobileMediaScale} bg-black`}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
                <video
                  key={`${activePhoto.url}-desktop`}
                  src={activePhoto.url}
                  poster={activePhoto.poster}
                  className={`${compactLayout ? 'hidden' : 'hidden md:block'} w-full h-full object-cover`}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </>
            ) : (
              <>
                <img
                  key={activePhoto.url}
                  src={activePhoto.url}
                  alt={activePhoto.title || 'Legacy moment'}
                  className={`${compactLayout ? 'block' : 'md:hidden'} w-full h-full ${mobileMediaFit} ${mobileMediaPosition} ${mobileMediaScale} bg-black`}
                  style={
                    shouldKenBurns
                      ? { animation: 'breathingScale 10s ease-in-out infinite', willChange: 'transform' }
                      : undefined
                  }
                />
                <img
                  key={`${activePhoto.url}-desktop`}
                  src={activePhoto.url}
                  alt={activePhoto.title || 'Legacy moment'}
                  className={`${compactLayout ? 'hidden' : 'hidden md:block'} w-full h-full object-cover`}
                  style={
                    shouldKenBurns
                      ? { animation: 'breathingScale 10s ease-in-out infinite', willChange: 'transform' }
                      : undefined
                  }
                />
              </>
            )}
          </div>

          {showBottomCaption && (
            <>
              {!compactLayout && (
                <>
                  <div
                    className={`absolute inset-y-0 right-0 hidden md:flex w-[22rem] lg:w-[24rem] justify-end transition-all duration-300 ${
                      sidebarOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
                    }`}
                  >
                    <div className="h-full w-full max-h-[100vh] overflow-y-auto bg-gradient-to-l from-black/85 via-black/55 to-transparent backdrop-blur-md border-l border-white/10 shadow-[-20px_0_50px_rgba(0,0,0,0.4)] px-6 py-8 text-white flex flex-col gap-6 pointer-events-auto">
                      <section className="space-y-4 flex-1 min-h-[14rem]">
                        <p className="text-xs uppercase tracking-[0.4em] text-emerald-200 drop-shadow">
                          {contextSubtitle || 'Legacy Moment'}
                        </p>
                        <div className="space-y-3">
                          <p className="text-lg font-semibold tracking-wide drop-shadow-lg">
                            {activePhoto.title || contextTitle || 'Captured Moment'}
                          </p>
                          {(activePhoto.narrative || contextSubtitle) && (
                            <p className="text-sm text-white/90 leading-relaxed drop-shadow">
                              {activePhoto.narrative || contextSubtitle}
                            </p>
                          )}
                        </div>
                      </section>
                      <section className="space-y-3">
                        <div className="border-t border-white/10 pt-4">
                          {timeline || <p className="text-xs text-white/50">Timeline coming soon.</p>}
                        </div>
                      </section>
                      <section className="mt-auto pt-4 border-t border-white/10 space-y-3 sticky bottom-0 bg-black/70 backdrop-blur-md -mx-6 px-6 pb-6">
                        <p className="text-[10px] uppercase tracking-[0.4em] text-emerald-200 drop-shadow">Presentation Controls</p>
                        {primaryControl ?? (
                          <button
                            type="button"
                            onClick={onPrimaryPlayToggle}
                            className="w-full flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-sm font-medium tracking-wide py-3 transition"
                          >
                            {primaryIsPlaying ? (
                              <>
                                <PauseIcon className="w-4 h-4" />
                                Pause Story
                              </>
                            ) : (
                              <>
                                <PlayIcon className="w-4 h-4" />
                                Play Story
                              </>
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={onSidebarToggle}
                          className="w-full rounded-full border border-white/20 bg-white/5 hover:bg-white/15 text-[10px] tracking-[0.4em] uppercase py-2 transition"
                        >
                          {sidebarOpen ? 'Hide Sidebar' : 'Show Sidebar'}
                        </button>
                      </section>
                    </div>
                  </div>
                  {!sidebarOpen && (
                    <div
                      className={`hidden md:flex absolute bottom-6 right-6 z-20 w-[22rem] justify-end px-6 transition-opacity duration-300 ${
                        uiVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={onSidebarShow}
                        className="w-full rounded-full border border-white/20 bg-white/5 hover:bg-white/15 text-[10px] tracking-[0.4em] uppercase py-2 transition text-white"
                      >
                        Show Sidebar
                      </button>
                    </div>
                  )}
                </>
              )}
              {compactLayout && (
                <div
                  className={`absolute inset-x-4 bottom-3 z-30 transition-all duration-300 ${
                    isPortrait ? 'opacity-100' : uiVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <div className="pointer-events-auto rounded-3xl bg-white/10 backdrop-blur-md border border-white/15 shadow-[0_14px_28px_rgba(0,0,0,0.35)] px-4 pt-3 pb-2 text-white max-w-[78vw] mx-auto">
                    <div className="flex items-start gap-4">
                      <div className="min-w-0 flex-1 space-y-2">
                        <p className="text-[10px] uppercase tracking-[0.35em] text-white/55">
                          {contextSubtitle || 'Legacy Story'}
                        </p>
                        <p className="text-base font-semibold tracking-wide">
                          {activePhoto.title || contextTitle || 'Captured Moment'}
                        </p>
                        {activePhoto.narrative && (
                          <p
                            className={`text-xs text-white/70 leading-relaxed ${
                              isPortrait ? 'max-h-24 overflow-auto' : 'max-h-16 overflow-hidden'
                            }`}
                          >
                            {activePhoto.narrative}
                          </p>
                        )}
                      </div>
                    </div>
                    {timelineYears && timelineYears.length > 0 && (
                      <div className="mt-2 border-t border-white/10 pt-2 text-[10px] text-white/70">
                        <div className="mb-2 text-[10px] text-emerald-300 flex items-center justify-between gap-2">
                          <div className="uppercase tracking-[0.35em]">
                            {timelineYearsHeading || 'Program Timeline'}
                          </div>
                          {activeTimelineEntry && (
                            <div className="text-emerald-200/90">
                              {activeTimelineEntry.year}
                            </div>
                          )}
                        </div>
                        {activeTimelineEntry && timelineYearsSubheading && (
                          <div className="text-emerald-200/90 mb-2">{timelineYearsSubheading}</div>
                        )}
                        <div className="space-y-2">
                          <div>
                            <div className="flex items-center justify-between text-[10px] text-white/60">
                              <span className="uppercase tracking-[0.3em]">Overall Progress</span>
                              <span>{overallCompleted}/{totalPhotos} • {overallTimeLabel}</span>
                            </div>
                            <div
                              className={`mt-1 h-1 rounded-full bg-white/10 overflow-hidden relative ${
                                onTimelinePhotoSelect ? 'cursor-pointer' : ''
                              }`}
                              onClick={handleTimelineClick}
                            >
                              {overallTicks.map((tick) => (
                                <span
                                  key={`timeline-tick-${tick.year}`}
                                  className="absolute top-0 h-full w-px bg-white/20"
                                  style={{ left: `${Math.round(tick.ratio * 100)}%` }}
                                />
                              ))}
                              <div
                                className="h-full bg-emerald-300/90 transition-[width] duration-700 ease-out shadow-[0_0_10px_rgba(16,185,129,0.65)]"
                                style={{ width: `${Math.round(overallProgress * 100)}%` }}
                              />
                            </div>
                            {yearSegments.length > 0 && (
                              <div className="relative mt-2 h-6 text-[9px] text-emerald-200/90">
                                {yearSegments.map((segment, index) => {
                                  const ratio = segment.startRatio;
                                  const transform =
                                    ratio < 0.05
                                      ? 'translateX(0)'
                                      : ratio > 0.95
                                        ? 'translateX(-100%)'
                                        : 'translateX(-50%)';
                                  const rowTop = index % 2 === 0 ? '0px' : '12px';
                                  return (
                                    <button
                                      key={`timeline-year-${segment.year}`}
                                      type="button"
                                      onClick={() => onTimelineYearSelect?.(segment.year)}
                                      className="absolute whitespace-nowrap hover:text-emerald-100 transition leading-none"
                                      style={{
                                        left: `${Math.round(ratio * 100)}%`,
                                        transform,
                                        top: rowTop,
                                      }}
                                    >
                                      {segment.year}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex justify-end">
                          <button
                            type="button"
                            onClick={onCompactPlayToggle}
                            className="h-10 w-10 rounded-full bg-emerald-500 text-white shadow-[0_8px_18px_rgba(16,185,129,0.45)] flex items-center justify-center transition hover:bg-emerald-600"
                            aria-label={compactIsPlaying ? 'Pause story' : 'Play story'}
                          >
                            {compactIsPlaying ? (
                              <PauseIcon className="w-4 h-4" />
                            ) : (
                              <PlayIcon className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {canNavigate && (
            <>
              <button
                type="button"
                onClick={onPrevAction}
                className={`absolute ${
                  compactLayout
                    ? 'left-4 top-[calc(50%-60px)] -translate-y-1/2'
                    : fullscreen
                    ? 'left-12 md:left-16 top-1/2 -translate-y-1/2'
                    : 'left-4 top-1/2 -translate-y-1/2'
                } z-40 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 transition-opacity duration-300 ${
                  compactLayout ? 'opacity-100' : uiVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                aria-label={prevAriaLabel}
                disabled={prevDisabled}
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={onNextAction}
                className={`absolute ${
                  compactLayout
                    ? 'right-4 top-[calc(50%-60px)] -translate-y-1/2'
                    : fullscreen
                    ? 'right-12 md:right-16 top-1/2 -translate-y-1/2'
                    : 'right-4 top-1/2 -translate-y-1/2'
                } z-40 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 transition-opacity duration-300 ${
                  compactLayout ? 'opacity-100' : uiVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                aria-label={nextAriaLabel}
                disabled={nextDisabled}
              >
                <ArrowRightIcon className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

const StoryGallery: React.FC<StoryGalleryProps> = ({
  photos,
  contextTitle,
  contextSubtitle,
  autoplay = false,
  onCycleComplete,
  onPlayChange,
  externalPlayState,
  fullscreen = false,
  onPhotoChange,
  showBottomCaption = true,
  timeline,
  timelineYears,
  timelineYearsHeading,
  timelineYearsSubheading,
  onTimelineYearSelect,
  compactPlayState,
  onCompactPlayToggle,
  kenBurnsActive,
  tintOverlay = false,
  advanceSignal,
  primaryControl,
  instanceId,
  requestedPhotoIndex,
  sidebarRevealSignal,
  sidebarRevealDurationMs = 5000,
  onPrevGroup,
  onNextGroup,
  overallTotalSeconds,
  overallRemainingSeconds,
  durationLoadedCount,
  durationExpectedCount,
  onTimelinePhotoSelect,
}) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [internalPlay, setInternalPlay] = useState(autoplay);
  const [uiVisible, setUiVisible] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { isCompactLayout, forceCoverMedia, isPortrait } = useCompactLayout();
  const hideUiTimer = useRef<NodeJS.Timeout | null>(null);
  const sidebarRevealTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoOpenedSidebarRef = useRef(false);
  const lastSidebarRevealSignalRef = useRef<number | undefined>(undefined);
  const prevPhotoRef = useRef<StoryPhoto | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const startHideTimer = useCallback(() => {
    if (hideUiTimer.current) {
      clearTimeout(hideUiTimer.current);
    }
    hideUiTimer.current = setTimeout(() => setUiVisible(false), 3000);
  }, []);

  const handleUserActivity = useCallback(() => {
    setUiVisible(true);
    startHideTimer();
  }, [startHideTimer]);

  useEffect(() => {
    startHideTimer();
    return () => {
      if (hideUiTimer.current) {
        clearTimeout(hideUiTimer.current);
      }
    };
  }, [startHideTimer]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    photos.forEach((photo) => {
      if ((photo.type ?? 'image') === 'image' && photo.url) {
        const img = new Image();
        img.src = photo.url;
      }
      if (photo.poster) {
        const poster = new Image();
        poster.src = photo.poster;
      }
    });
  }, [photos]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;
    const updateViewportVars = () => {
      root.style.setProperty('--story-vh', `${window.innerHeight}px`);
      root.style.setProperty('--story-vw', `${window.innerWidth}px`);
    };
    updateViewportVars();
    window.addEventListener('resize', updateViewportVars);
    window.addEventListener('orientationchange', updateViewportVars);
    return () => {
      window.removeEventListener('resize', updateViewportVars);
      window.removeEventListener('orientationchange', updateViewportVars);
    };
  }, []);

  useEffect(() => {
    const handleKey = () => handleUserActivity();
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleUserActivity]);


  const isPlaying = externalPlayState !== undefined ? externalPlayState : internalPlay;
  const shouldKenBurns = kenBurnsActive ?? isPlaying;

  const activePhoto = photos[photoIndex];
  const activeMediaType = activePhoto?.type ?? 'image';
  const goPrev = useCallback(
    () => setPhotoIndex((prev) => (prev === 0 ? Math.max(photos.length - 1, 0) : prev - 1)),
    [photos.length]
  );
  const goNext = useCallback(() => {
    setPhotoIndex((prev) => {
      const nextIndex = prev === photos.length - 1 ? 0 : prev + 1;
      if (nextIndex === 0 && prev === photos.length - 1 && onCycleComplete) {
        onCycleComplete();
      }
      return nextIndex;
    });
  }, [onCycleComplete, photos.length]);

  const prevAdvanceSignal = useRef<number | undefined>(advanceSignal);
  useEffect(() => {
    if (advanceSignal === undefined) return;
    if (prevAdvanceSignal.current === undefined) {
      prevAdvanceSignal.current = advanceSignal;
      if (advanceSignal > 0) {
        goNext();
      }
      return;
    }
    if (advanceSignal !== prevAdvanceSignal.current) {
      prevAdvanceSignal.current = advanceSignal;
      goNext();
    }
  }, [advanceSignal, goNext]);

  useEffect(() => {
    if (activePhoto) {
      onPhotoChange?.(activePhoto, photoIndex, instanceId);
    }
  }, [activePhoto, photoIndex, onPhotoChange, instanceId]);

  useEffect(() => {
    if (!isPlaying || photos.length <= 1) return;
    const timer = setInterval(() => {
      setPhotoIndex((prev) => {
        const nextIndex = prev === photos.length - 1 ? 0 : prev + 1;
        if (nextIndex === 0 && prev === photos.length - 1 && onCycleComplete) {
          onCycleComplete();
        }
        return nextIndex;
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [isPlaying, photos.length, onCycleComplete]);

  useEffect(() => {
    if (externalPlayState === undefined) return;
    setInternalPlay(externalPlayState);
    setPhotoIndex(0);
  }, [externalPlayState]);

  useEffect(() => {
    setPhotoIndex(0);
  }, [photos]);

  useEffect(() => {
    if (sidebarRevealSignal === undefined) return;
    if (lastSidebarRevealSignalRef.current === sidebarRevealSignal) return;
    lastSidebarRevealSignalRef.current = sidebarRevealSignal;
    if (sidebarOpen) return;
    setSidebarOpen(true);
    autoOpenedSidebarRef.current = true;
    if (sidebarRevealTimerRef.current) {
      clearTimeout(sidebarRevealTimerRef.current);
    }
    sidebarRevealTimerRef.current = setTimeout(() => {
      if (autoOpenedSidebarRef.current) {
        setSidebarOpen(false);
        autoOpenedSidebarRef.current = false;
      }
      sidebarRevealTimerRef.current = null;
    }, sidebarRevealDurationMs);
  }, [sidebarRevealSignal, sidebarRevealDurationMs, sidebarOpen]);

  useEffect(() => {
    return () => {
      if (sidebarRevealTimerRef.current) {
        clearTimeout(sidebarRevealTimerRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (requestedPhotoIndex === null || requestedPhotoIndex === undefined) return;
    if (requestedPhotoIndex === photoIndex) return;
    const nextIndex = Math.min(Math.max(requestedPhotoIndex, 0), Math.max(photos.length - 1, 0));
    setPhotoIndex(nextIndex);
  }, [requestedPhotoIndex, photoIndex, photos.length]);

  const prevExternalPlayRef = useRef(externalPlayState);
  useEffect(() => {
    if (externalPlayState && !prevExternalPlayRef.current) {
      setPhotoIndex(0);
    }
    prevExternalPlayRef.current = externalPlayState;
  }, [externalPlayState, photos.length]);

  useEffect(() => {
    if (!activePhoto) return;
    prevPhotoRef.current = activePhoto;
  }, [activePhoto]);

  const compactLayout = fullscreen && isCompactLayout;
  const mobileMediaFit = forceCoverMedia ? 'object-cover' : 'object-contain';
  const mobileMediaPosition = isPortrait ? 'object-top' : 'object-center';
  const mobileMediaScale = isPortrait ? 'scale-125 origin-top' : '';
  const activeTimelineEntry = timelineYears?.find((entry) => entry.isActive);
  const compactIsPlaying = (compactPlayState ?? isPlaying) === true;
  const handlePrevAction = useCallback(() => {
    if (photos.length <= 1 && onPrevGroup) {
      onPrevGroup();
      return;
    }
    if (photoIndex === 0 && onPrevGroup) {
      onPrevGroup();
      return;
    }
    goPrev();
  }, [goPrev, onPrevGroup, photoIndex, photos.length]);

  const handleNextAction = useCallback(() => {
    if (photos.length <= 1 && onNextGroup) {
      onNextGroup();
      return;
    }
    if (photoIndex === photos.length - 1 && onNextGroup) {
      onNextGroup();
      return;
    }
    goNext();
  }, [goNext, onNextGroup, photoIndex, photos.length]);

  const handlePrimaryPlayToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (externalPlayState === undefined) {
        setInternalPlay((prev) => !prev);
      }
      onPlayChange?.(!isPlaying);
    },
    [externalPlayState, isPlaying, onPlayChange]
  );

  const handleCompactPlayToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (onCompactPlayToggle) {
        onCompactPlayToggle();
        return;
      }
      if (externalPlayState === undefined) {
        setInternalPlay((prev) => !prev);
      }
      onPlayChange?.(!isPlaying);
    },
    [externalPlayState, isPlaying, onCompactPlayToggle, onPlayChange]
  );

  const handleSidebarToggle = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    autoOpenedSidebarRef.current = false;
    if (sidebarRevealTimerRef.current) {
      clearTimeout(sidebarRevealTimerRef.current);
      sidebarRevealTimerRef.current = null;
    }
    setSidebarOpen((prev) => !prev);
  }, []);

  const handleSidebarShow = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSidebarOpen(true);
  }, []);

  const handlePrevClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      handlePrevAction();
    },
    [handlePrevAction]
  );

  const handleNextClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      handleNextAction();
    },
    [handleNextAction]
  );

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      handleUserActivity();
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    },
    [handleUserActivity]
  );

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (!start) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      if (deltaX < 0) {
        handleNextAction();
      } else {
        handlePrevAction();
      }
    },
    [handleNextAction, handlePrevAction]
  );

  if (!activePhoto) return null;

  return (
    <StoryGalleryView
      photosLength={photos.length}
      activePhoto={activePhoto}
      activeMediaType={activeMediaType}
      compactLayout={compactLayout}
      fullscreen={fullscreen}
      mobileMediaFit={mobileMediaFit}
      mobileMediaPosition={mobileMediaPosition}
      mobileMediaScale={mobileMediaScale}
      shouldKenBurns={shouldKenBurns}
      showBottomCaption={showBottomCaption}
      sidebarOpen={sidebarOpen}
      uiVisible={uiVisible}
      isPortrait={isPortrait}
      contextSubtitle={contextSubtitle}
      contextTitle={contextTitle}
      activeTimelineEntry={activeTimelineEntry}
      timelineYears={timelineYears}
      timelineYearsHeading={timelineYearsHeading}
      timelineYearsSubheading={timelineYearsSubheading}
      timeline={timeline}
      primaryControl={primaryControl}
      primaryIsPlaying={isPlaying}
      compactIsPlaying={compactIsPlaying}
      overallTotalSeconds={overallTotalSeconds}
      overallRemainingSeconds={overallRemainingSeconds}
      durationLoadedCount={durationLoadedCount}
      durationExpectedCount={durationExpectedCount}
      onTimelineYearSelect={onTimelineYearSelect}
      onTimelinePhotoSelect={onTimelinePhotoSelect}
      onPrimaryPlayToggle={handlePrimaryPlayToggle}
      onCompactPlayToggle={handleCompactPlayToggle}
      onSidebarToggle={handleSidebarToggle}
      onSidebarShow={handleSidebarShow}
      onPrevAction={handlePrevClick}
      onNextAction={handleNextClick}
      canNavigate={photos.length > 1 || Boolean(onPrevGroup) || Boolean(onNextGroup)}
      prevDisabled={photos.length <= 1 && !onPrevGroup}
      nextDisabled={photos.length <= 1 && !onNextGroup}
      prevAriaLabel={photos.length <= 1 ? 'Previous year' : 'Previous media'}
      nextAriaLabel={photos.length <= 1 ? 'Next year' : 'Next media'}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onPointerDown={handleUserActivity}
      onMouseMove={handleUserActivity}
    />
  );
};

export default StoryGallery;
